import React from "react";
import { Phone, Mail, MapPin, Globe, Facebook } from "lucide-react";
import { SITE, TOWNS } from "../site.config";

/* Brand-coloured social icon row */
function SocialBadges() {
  const base =
    "h-11 w-11 inline-flex items-center justify-center rounded-full shadow-md hover:-translate-y-0.5 transition-transform";
  return (
    <div className="mt-4 flex items-center gap-2.5">
      {/* Facebook */}
      <a
        data-testid="footer-facebook"
        href={SITE.facebookUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Find us on Facebook"
        title="Facebook"
        className={`${base} text-white`}
        style={{ backgroundColor: "#1877F2" }}
      >
        <Facebook size={20} fill="currentColor" strokeWidth={0} />
      </a>

      {/* Google reviews */}
      <a
        data-testid="footer-google"
        href={SITE.googleUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Google reviews"
        title="Google reviews"
        className={`${base} bg-white`}
      >
        <GoogleGIcon />
      </a>

      {/* Checkatrade */}
      <a
        data-testid="footer-checkatrade"
        href={SITE.checkatradeUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Find us on Checkatrade"
        title="Checkatrade"
        className={base}
        style={{ backgroundColor: "#FFCC00" }}
      >
        <CheckatradeIcon />
      </a>

      {/* Trustpilot */}
      <a
        data-testid="footer-trustpilot"
        href={SITE.trustpilotUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Find us on Trustpilot"
        title="Trustpilot"
        className={base}
        style={{ backgroundColor: "#00B67A" }}
      >
        <TrustpilotIcon />
      </a>
    </div>
  );
}

/* Inline SVG: Google "G" multi-coloured */
function GoogleGIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 48 48"
      aria-hidden="true"
      className="flex-shrink-0"
    >
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8a12 12 0 1 1 0-24c3.1 0 6 1.2 8.1 3.1l5.7-5.7C34.5 6.4 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 6 1.2 8.1 3.1l5.7-5.7C34.5 6.4 29.5 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.4 0 10.3-2.1 14-5.5l-6.5-5.5c-2 1.5-4.6 2.4-7.5 2.4-5.3 0-9.7-3.4-11.3-8L6 32.5C9.3 39.1 16 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.5l6.5 5.5C42.6 35.7 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

/* Inline SVG: Checkatrade red tick on yellow */
function CheckatradeIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="flex-shrink-0"
    >
      <path
        d="M5 12.5l4 4L19 6.5"
        stroke="#E30613"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/* Inline SVG: Trustpilot white star */
function TrustpilotIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="flex-shrink-0"
    >
      <polygon
        fill="#FFFFFF"
        points="12,2 14.91,8.42 22,9.27 16.5,13.97 18,21 12,17.27 6,21 7.5,13.97 2,9.27 9.09,8.42"
      />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-moffat-charcoal text-white pt-14 pb-10 border-t-4 border-moffat-red"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={SITE.logoUrl}
                alt="A.Moffat Flooring logo"
                className="h-14 w-14 rounded-full bg-white p-0.5"
              />
              <div className="leading-none">
                <p className="font-display text-lg">CARPET &amp; LINO</p>
                <p className="text-xs font-bold text-moffat-red tracking-[0.18em] uppercase mt-1">
                  Fitted by A.Moffat
                </p>
              </div>
            </div>
            <p className="mt-5 text-white/70 text-sm leading-relaxed max-w-sm">
              Quality flooring fitted to perfection. Carpets, Lino, Vinyl, LVT,
              floor preparation and more — proudly serving County Durham &amp; the
              North East for over 20 years.
            </p>

            <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.18em] text-moffat-red">
              Find us on
            </p>
            <SocialBadges />
          </div>

          <div className="md:col-span-4">
            <p className="font-script text-2xl text-moffat-red leading-none">
              Get in touch
            </p>
            <ul className="mt-3 space-y-3 text-sm">
              <li>
                <a
                  data-testid="footer-call"
                  href={`tel:${SITE.phoneTel}`}
                  className="flex items-center gap-3 text-white/90 hover:text-white"
                >
                  <Phone
                    size={16}
                    className="text-moffat-red"
                    strokeWidth={2.5}
                  />
                  <span className="font-semibold">Call us today</span>
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-whatsapp"
                  href={SITE.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center hover:opacity-90 transition-opacity"
                >
                  <img
                    src={SITE.whatsappButtonImg}
                    alt="Chat on WhatsApp"
                    className="h-10 w-auto rounded-full"
                  />
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-email"
                  href={`mailto:${SITE.email}`}
                  className="flex items-center gap-3 text-white/90 hover:text-white"
                >
                  <Mail
                    size={16}
                    className="text-moffat-red"
                    strokeWidth={2.5}
                  />
                  <span className="font-semibold">Email us</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/90">
                <MapPin
                  size={16}
                  className="text-moffat-red"
                  strokeWidth={2.5}
                />
                <span className="font-semibold">{SITE.coverage}</span>
              </li>
              <li>
                <a
                  data-testid="footer-website"
                  href={SITE.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/90 hover:text-white"
                >
                  <Globe
                    size={16}
                    className="text-moffat-red"
                    strokeWidth={2.5}
                  />
                  <span className="font-semibold">{SITE.website}</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="font-script text-2xl text-moffat-red leading-none">
              Areas covered
            </p>
            <p className="mt-3 text-sm text-white/70 leading-relaxed">
              {TOWNS.join(" · ")} and surrounding areas.
            </p>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/60">
            © {new Date().getFullYear()} Carpet &amp; Lino Fitted by A.Moffat.
            All rights reserved.
          </p>
          <p className="font-display text-xs text-white/70 tracking-widest">
            QUALITY WORK • GREAT FINISH • BUILT TO LAST
          </p>
        </div>
      </div>
    </footer>
  );
}
